import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Instagram } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", service: "", message: "", website: "" });
  const [privacy, setPrivacy] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) {
      toast.error("Devi dichiarare di aver letto l'Informativa Privacy per inviare la richiesta.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Richiesta inviata. Ti ricontatteremo a breve.");
      setForm({ name: "", business: "", email: "", phone: "", service: "", message: "", website: "" });
      setPrivacy(false);
    } catch {
      toast.error("Qualcosa è andato storto. Riprova o scrivici direttamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="relative py-28 lg:py-40">
      <div className="mx-auto grid max-w-[1440px] gap-20 px-6 lg:grid-cols-5 lg:px-12">
        <Reveal className="lg:col-span-2">
          <Overline className="mb-6">Contatti</Overline>
          <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
            Raccontaci il tuo <span className="gold-text italic">progetto.</span>
          </h2>
          <p className="mt-8 max-w-sm text-base font-light leading-relaxed text-[#A3A3A3]">
            Compila il modulo e ti risponderemo entro 24 ore con una proposta su misura per il tuo locale.
          </p>
          <div className="mt-12 space-y-5">
            <a href="mailto:recensio.it@gmail.com" data-testid="contact-email-link" className="group flex items-center gap-4 text-sm font-light text-[#c9c9c9] transition-colors duration-500 hover:text-[#E5CAA4]">
              <Mail size={17} strokeWidth={1.2} className="text-[#C19D60]" /> recensio.it@gmail.com
            </a>
            <a href="https://instagram.com/recensio.italia" target="_blank" rel="noreferrer" data-testid="contact-instagram-link" className="group flex items-center gap-4 text-sm font-light text-[#c9c9c9] transition-colors duration-500 hover:text-[#E5CAA4]">
              <Instagram size={17} strokeWidth={1.2} className="text-[#C19D60]" /> @recensio.italia
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form onSubmit={submit} data-testid="quote-form" className="grid gap-9 sm:grid-cols-2">
            <input required className="lux-input" data-testid="quote-name-input" placeholder="Nome e cognome *" value={form.name} onChange={set("name")} />
            <input className="lux-input" data-testid="quote-business-input" placeholder="Nome del locale (facoltativo)" value={form.business} onChange={set("business")} />
            <input required type="email" className="lux-input" data-testid="quote-email-input" placeholder="Email *" value={form.email} onChange={set("email")} />
            <input className="lux-input" data-testid="quote-phone-input" placeholder="Telefono (facoltativo)" value={form.phone} onChange={set("phone")} />
            <select className="lux-input" data-testid="quote-service-select" value={form.service} onChange={set("service")}>
              <option value="">Cosa ti interessa? (facoltativo)</option>
              <option>Menu Digitale</option>
              <option>Sito Web</option>
              <option>NFC & Recensioni</option>
              <option>Piastrine NFC</option>
              <option>Fotografia</option>
              <option>Video & Drone</option>
              <option>Progetto completo</option>
            </select>
            <div className="sm:col-span-2">
              <textarea required rows={4} className="lux-input resize-none" data-testid="quote-message-input" placeholder="Raccontaci cosa vuoi migliorare *" value={form.message} onChange={set("message")} />
            </div>
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={form.website} onChange={set("website")} data-testid="quote-honeypot" />
            <div className="sm:col-span-2">
              <label className="flex cursor-pointer items-start gap-3 text-xs font-light leading-relaxed text-[#A3A3A3]" data-testid="quote-privacy-label">
                <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="quote-privacy-checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[#C19D60]" />
                <span>
                  Dichiaro di aver letto l'<a href="/privacy" target="_blank" rel="noreferrer" className="text-[#E5CAA4] underline underline-offset-4">Informativa Privacy</a> relativa al trattamento dei dati personali. *
                </span>
              </label>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="quote-submit-btn"
                className="group relative overflow-hidden rounded-[5px] bg-[#C19D60] px-10 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#050505] disabled:opacity-60"
              >
                <span className="absolute inset-0 origin-left scale-x-0 bg-[#E5CAA4] transition-transform duration-500 ease-out group-hover:scale-x-100" />
                <span className="relative flex items-center gap-3">
                  {sending ? "Invio in corso…" : "Invia la richiesta"} <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
                </span>
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
